<template>
    <template v-if="sideContent">
        <SideContentComponent :content="sideContent" next-image="/signup-client-front/logo-clients.webp" />
        <LazyIdleMaltImg
            v-for="(image, index) in preloadedImages"
            :key="`preloaded-image-${index}`"
            alt=""
            width="319"
            style="visibility: hidden; height: 0; position: fixed"
            :src="image"
            loading="lazy"
            hydrate-on-idle
        />
    </template>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg as LazyMaltImg} from '#components';
    import {useFunnelSteps, useLocale, useTranslation} from '#imports';
    import {FunnelPageName} from '~/lib/steps';
    import {useContextStore} from '~/store/context.store';
    import {useUserQualificationStore} from '~/store/user-qualification.store';
    import SideContentComponent from './SideContent.vue';

    // Types
    export type SideContent = {
        title: string;
        imageUrl: string;
        body: Array<string>;
    };

    // Stores
    const contextStore = useContextStore();
    const {edrMatches} = storeToRefs(contextStore);

    const userQualificationStore = useUserQualificationStore();
    const {currentQuestionIsLast} = storeToRefs(userQualificationStore);

    // Composables
    const {t} = useTranslation();
    const {languageCode} = useLocale();
    const {currentPage} = useFunnelSteps();

    // Computed values
    const sideContent = computed((): SideContent | undefined => getSideContent(currentPage.value?.name));

    const preloadedImages = [
        '/signup-client-front/best-freelancers.webp',
        `/signup-client-front/approved-quote-${languageCode.value}.webp`,
        `/signup-client-front/secure-payments-${languageCode.value}.webp`,
        '/signup-client-front/logo-clients.webp',
        `/signup-client-front/analytics-${languageCode.value}.webp`,
        `/signup-client-front/reviews-${languageCode.value}.png`,
        `/signup-client-front/freelancer-profile-${languageCode.value}.webp`,
    ];

    function getSideContent(pageName: FunnelPageName | undefined): SideContent | undefined {
        switch (pageName) {
            case FunnelPageName.COMPANY_CHOICE:
                return {
                    title: t('signup.client.user-details.page.side.content.title'),
                    imageUrl: `/signup-client-front/secure-payments-${languageCode.value}.webp`,
                    body: [
                        t('signup.client.user-details.page.side.content.benefit.1'),
                        t('signup.client.user-details.page.side.content.benefit.2'),
                        t('signup.client.user-details.page.side.content.benefit.3'),
                    ],
                };
            case FunnelPageName.COMPANY_DETAILS:
                return edrMatches.value
                    ? {
                          title: t('signup.client.company-details.edr.page.side.content.title'),
                          imageUrl: `/signup-client-front/analytics-${languageCode.value}.webp`,
                          body: [
                              t('signup.client.company-details.edr.page.side.content.benefit.1'),
                              t('signup.client.company-details.edr.page.side.content.benefit.2'),
                              t('signup.client.company-details.edr.page.side.content.benefit.3'),
                          ],
                      }
                    : {
                          title: t('signup.client.company-details.page.side.content.title'),
                          imageUrl: '/signup-client-front/logo-clients.webp',
                          body: [t('signup.client.company-details.page.side.content.benefit.1')],
                      };
            case FunnelPageName.EMAIL:
                return {
                    title: t('signup.client.email.page.side.content.title'),
                    imageUrl: '/signup-client-front/best-freelancers.webp',
                    body: [
                        t('signup.client.email.page.side.content.benefit.1'),
                        t('signup.client.email.page.side.content.benefit.2'),
                        t('signup.client.email.page.side.content.benefit.3'),
                    ],
                };
            case FunnelPageName.EMAIL_VALIDATION:
                return {
                    title: t('signup.client.email-verification.page.side.content.title'),
                    imageUrl: `/signup-client-front/approved-quote-${languageCode.value}.webp`,
                    body: [t('signup.client.email-verification.page.side.content.benefit.1')],
                };
            case FunnelPageName.USER_DETAILS:
                if (edrMatches.value) {
                    return {
                        title: t('signup.client.company-details.page.side.content.title'),
                        imageUrl: '/signup-client-front/logo-clients.webp',
                        body: [t('signup.client.company-details.page.side.content.benefit.1')],
                    };
                } else {
                    return getSideContent(FunnelPageName.COMPANY_CHOICE);
                }
            case FunnelPageName.USER_QUALIFICATION:
                return currentQuestionIsLast.value
                    ? {
                          title: t('signup.client.user-qualification.page.last.question.side.content.title'),
                          imageUrl: `/signup-client-front/freelancer-profile-${languageCode.value}.webp`,
                          body: [
                              t('signup.client.user-qualification.page.last.question.side.content.benefit.1'),
                              t('signup.client.user-qualification.page.last.question.side.content.benefit.2'),
                              t('signup.client.user-qualification.page.last.question.side.content.benefit.3'),
                          ],
                      }
                    : {
                          title: t('signup.client.user-qualification.page.first.question.side.content.title'),
                          imageUrl: `/signup-client-front/reviews-${languageCode.value}.png`,
                          body: [t('signup.client.user-qualification.page.first.question.side.content.benefit.1')],
                      };
            default:
                return undefined;
        }
    }
</script>
